// httpClient — the shell's one impure contact with the server's REST API.
//
// The server is a command+query envelope API (docs/model/foundation/backend):
//   • POST /commands/:name — parsed by @bilda/app's contract; returns the
//     workflow Result as `{ ok: true, value } | { ok: false, error }`, HTTP
//     200 for BOTH (the envelope's `ok` is the success discriminator). HTTP
//     4xx/5xx is reserved for PROTOCOL faults (bad JSON body, unknown command,
//     uncaught crash).
//   • GET /state — the same envelope around a resource summary.
//
// This module fetches, then PARSES the failure once into the shell's single
// typed error vocabulary (`ExternalError`) — "parse, don't validate" at the
// boundary. It returns the envelope's `value` as `unknown`; the caller's
// adapter parses that `unknown` into its typed shape (see materialAdapter /
// conversationAdapter). The `ok` discriminant is read STRUCTURALLY (no cast),
// mirroring `@bilda/core`'s untrusted-JSON idiom.
//
// vite proxies `/commands`, `/state` and `/templates` to the dev server, so
// these are same-origin calls — no base URL, no CORS, no cookie needed in
// fs-mode (mkServer mounts them without an auth guard).

export type { AuthProbe, OriginalBytes };
export {
    API_BASE,
    deleteAccount,
    getAdmin,
    getAuthMe,
    getClaims,
    getEntityHistory,
    getFileHistory,
    getHead,
    getHistory,
    getQuality,
    getResourceVersion,
    getSearch,
    getSourceOriginal,
    getState,
    getTemplates,
    getWorkspaceMembers,
    getWorkspaces,
    inviteWorkspaceMember,
    logout,
    openGenerationStream,
    openLogHeadStream,
    openTurnStream,
    postAdmin,
    postCommand,
    postSnapshot,
    postUpload,
    readEnvelope,
    removeWorkspaceMember,
    sourceOriginalUrl,
    TURN_REQUEST_TIMEOUT_MS,
    UPLOAD_REQUEST_TIMEOUT_MS,
};

import { Result } from "@bilda/utils";
import { i18n } from "@lingui/core";
import { isLocale } from "../i18n/supportedLocales.js";
import { classifyWireErrorType, isInfraWireType } from "./errorClass.js";
import {
    type ExternalError,
    mkForbidden,
    mkMalformed,
    mkPrivacyTermsNotAccepted,
    mkQuotaExhausted,
    mkRateLimited,
    mkRefused,
    mkRejected,
    mkUnauthorized,
    mkUnknown,
    mkUnreachable,
    mkUpstream,
} from "./externalError.js";
import { fields, list, str } from "./wireShape.js";

// The server's data/command API lives under `/v1` (client owns `/*` for SPA
// routing). Every API path in this module — and the handful of <a href>
// download sites outside it — prefixes with this ONE constant, so the base
// is defined in a single place. `request` applies it centrally; the three
// direct-`fetch`/`EventSource` sites below apply it inline.
const API_BASE = "/v1";

// Map a non-2xx HTTP status into the transport-fault vocabulary. Only reached
// for PROTOCOL errors — a refused workflow returns 200 + `ok:false` and is
// handled by `readEnvelope`, not here. A 403 is plain Forbidden unless its
// typed body says the privacy terms are not accepted (the consent gate).
const fromStatus = (status: number, body: unknown): ExternalError => {
    const detail = safeStringify(body);
    if (status === 401) return mkUnauthorized();
    if (status === 402) return mkQuotaExhausted();
    if (status === 403)
        return str(fields(fields(body)?.get("error"))?.get("type")) ===
            "privacy_terms_not_accepted"
            ? mkPrivacyTermsNotAccepted()
            : mkForbidden();
    if (status === 429) return mkRateLimited();
    if (status >= 500) return mkUpstream(detail);
    if (status >= 400) return mkRejected(detail);
    return mkUnknown(detail);
};

// Parse the refusing leaf out of the envelope's `error` — the un-flatten
// (error-handling.md gap 1). The wire carries @bilda/app's workflow
// vocabulary, whose `*_refused` wrappers nest the domain leaf as `cause`;
// prefer that leaf (more precise), else classify the wrapper itself.
// Infra families (append/cascade/adapter faults) are the server failing,
// not the input — they surface as Upstream. `undefined` = an error shape
// this vocabulary doesn't know; the caller keeps the generic fallback.
const refusalOf = (
    error: unknown,
    detail: string,
): ExternalError | undefined => {
    const f = fields(error);
    const wireType = str(f?.get("type"));
    if (wireType === undefined) return undefined;
    const cause = fields(f?.get("cause"));
    const causeType = str(cause?.get("type"));
    // The element's text now, on an element-changed refusal.
    const current = str(fields(cause?.get("current"))?.get("text"));
    const parts = list(f?.get("parts")).flatMap((p) => {
        const title = str(fields(p)?.get("title"));
        return title === undefined ? [] : [title];
    });
    const refused = [causeType, wireType]
        .filter((t): t is string => t !== undefined)
        .flatMap((t) => {
            const errorClass = classifyWireErrorType(t);
            return errorClass === undefined
                ? []
                : [mkRefused(t, errorClass, detail, parts, current)];
        })
        .at(0);
    if (refused !== undefined) return refused;
    return isInfraWireType(wireType) ? mkUpstream(detail) : undefined;
};

// Narrow the parsed body to the `{ ok, value|error }` envelope without a cast.
// `ok:true` → the workflow's value (as `unknown`, for the caller to parse).
// `ok:false` → the refusal parsed into `Refused` (typed leaf + class) when
// the discriminant is recognised, else the generic Rejected — with the raw
// error JSON kept as `detail` for telemetry only (never rendered).
const readEnvelope = (body: unknown): Result.Result<unknown, ExternalError> => {
    const env = fields(body);
    if (env === undefined || !env.has("ok")) {
        return Result.err(mkMalformed("response was not an {ok,…} envelope"));
    }
    if (env.get("ok") === true) {
        if (!env.has("value")) {
            return Result.err(mkMalformed("ok envelope carried no value"));
        }
        return Result.ok(env.get("value"));
    }
    const error = env.get("error");
    const detail = env.has("error") ? safeStringify(error) : "workflow refused";
    return Result.err(refusalOf(error, detail) ?? mkRejected(detail));
};

// A hung read must not stall forever (audit D3 — the eternal spinner). Every
// request arms an AbortController; when it fires, `fetch` rejects with an
// AbortError that the catch below already folds into `Unreachable` — so a hung
// `/state` flips the health chip offline + backoff, exactly like a dead socket,
// with no special-casing. Conservative default (documented shortcut): the real
// p99.9 latency budget is an owner call (audit §6 Q4); 15s is safe for the
// small envelopes these routes return without tripping on a slow-but-live read.
const REQUEST_TIMEOUT_MS = 15_000;

// The turn commands (`sendTurn`, `offerOpeningMove`) are the exception: the
// server holds the POST open for the WHOLE multi-round turn (commands.ts awaits
// `app.sendTurn` before responding), so a course-authoring turn legitimately
// runs far past the 15s small-envelope budget above. Aborting it at 15s is the
// P0 bug — the client tears down a live turn and reports Unreachable. Peg the
// ceiling to the turn's OWN lifetime bound (`MAX_STREAM_LIFETIME_MS` in
// server/routes/turnStream.ts = 10 min, the tool-loop headroom): long enough to
// never cut a real turn, still finite so a genuinely hung turn eventually fails
// (audit D3 — no eternal spinner). Same magnitude as the server's bound, by
// design.
const TURN_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;

// Uploads are the other exception: the server stores the original AND extracts
// its text before it answers (routes/uploads.ts), so a large PDF legitimately
// takes well past 15s. Aborting it reported a live upload as "can't reach the
// server" (2026-10-21, a 20 MB book on staging). Same finite ceiling as a turn:
// never cuts a real upload, still fails a genuinely hung one.
const UPLOAD_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const abortAfter = (
    ms: number,
): { readonly signal: AbortSignal; readonly clear: () => void } => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), ms);
    return { signal: controller.signal, clear: () => clearTimeout(id) };
};

const request = async (
    path: string,
    init: RequestInit,
    timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Result.Result<unknown, ExternalError>> => {
    const timeout = abortAfter(timeoutMs);
    let res: Response;
    try {
        res = await fetch(`${API_BASE}${path}`, {
            ...init,
            headers: withUiLocale(init.headers),
            signal: timeout.signal,
        });
    } catch (e) {
        // Network layer never delivered a response (offline / DNS / CORS) OR the
        // timeout aborted a hung read — both are transient transport faults.
        timeout.clear();
        return Result.err(mkUnreachable(messageOf(e)));
    }
    timeout.clear();
    let body: unknown;
    try {
        body = await res.json();
    } catch (e) {
        // The app server ALWAYS answers JSON — every route envelopes via
        // c.json, crashes included. A failing status with a non-JSON body
        // therefore means an intermediary answered in the server's place
        // (dev proxy with a dead upstream, load balancer): the app never
        // saw the request — a connection fault, not a server fault. A 2xx
        // with a non-JSON body is Malformed.
        return res.ok
            ? Result.err(mkMalformed(messageOf(e)))
            : Result.err(mkUnreachable(`HTTP ${res.status} non-JSON body`));
    }
    if (!res.ok) {
        return Result.err(fromStatus(res.status, body));
    }
    return readEnvelope(body);
};

// `GET /commands/sendTurn/stream/:conversationId` — the SSE transport for a
// turn's live progress (.claude/analysis/perf-design-proposals.md §1). Native
// `EventSource`: same-origin, so cookies (the postgres+auth session) travel
// automatically exactly like `fetch`'s default credentials, no extra config.
// The CALLER owns the connection's lifecycle — open it right before the
// matching `POST /commands/sendTurn`, close it once that promise settles
// (ok or err); this helper does neither, it only opens.
const openTurnStream = (conversationId: string): EventSource =>
    new EventSource(
        `${API_BASE}/commands/sendTurn/stream/${encodeURIComponent(conversationId)}`,
    );

// `GET /commands/showGenerationJob/stream/:root` — a material's generation
// job feed: the current snapshot first (when a job exists), then a whole
// snapshot on every change. Same posture as `openTurnStream`: the caller owns
// the connection's lifecycle; this helper only opens.
const openGenerationStream = (root: string): EventSource =>
    new EventSource(
        `${API_BASE}/commands/showGenerationJob/stream/${encodeURIComponent(root)}`,
    );

// `GET /head/stream` — a `moved` message whenever the workspace's log moved
// (on any machine), and one on connect. Same posture: the caller owns the
// connection's lifecycle; this helper only opens.
const openLogHeadStream = (): EventSource =>
    new EventSource(`${API_BASE}/head/stream`);

// `timeoutMs` is per-call so the caller picks the ceiling appropriate to the
// route: the small-envelope default for most commands, `TURN_REQUEST_TIMEOUT_MS`
// for the long-held turn commands (see the constant's note).
//
// A small command on the default ceiling outlives the page (`keepalive`): an
// edit saved as the tab closes or reloads still arrives. Browsers cap all
// keepalive bodies in flight together at 64 KiB, so a large body and the
// long-held turns go without it (they would only eat that budget).
const KEEPALIVE_MAX_BODY = 16 * 1024;
const postCommand = (
    name: string,
    body: unknown,
    headers?: Readonly<Record<string, string>>,
    timeoutMs?: number,
): Promise<Result.Result<unknown, ExternalError>> => {
    const text = JSON.stringify(body);
    return request(
        `/commands/${name}`,
        {
            method: "POST",
            headers: { "content-type": "application/json", ...(headers ?? {}) },
            body: text,
            keepalive:
                timeoutMs === undefined && text.length <= KEEPALIVE_MAX_BODY,
        },
        timeoutMs,
    );
};

// `POST /uploads/*` — upload one file as the RAW request body (no base64, no
// JSON envelope). The filename rides the query string (percent-encoded); the
// media type is set by the browser from `File.type` (do NOT set content-type
// here, or the browser won't derive it — an empty type lets the server infer
// from the extension). Routes through the shared `request` so the timeout/abort
// guard and `{ok,value|error}` envelope parse are identical to every command;
// the caller parses the ok value exactly as it did off the command bus.
const postUpload = (
    path: string,
    file: File,
    filename: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(
        `${path}?filename=${encodeURIComponent(filename)}`,
        { method: "POST", body: file },
        UPLOAD_REQUEST_TIMEOUT_MS,
    );

const getState = (
    headers?: Readonly<Record<string, string>>,
): Promise<Result.Result<unknown, ExternalError>> =>
    request("/state", { method: "GET", headers: { ...(headers ?? {}) } });

// `GET /quality?ids=` — the pure linguistics + grounding evaluations of many
// materials in ONE read (not two commands per material; reads sit outside the
// command rate limit).
const getQuality = (
    ids: readonly string[],
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/quality?ids=${ids.map(encodeURIComponent).join(",")}`, {
        method: "GET",
    });

// `GET /head` — the event-log head, the heartbeat's cheap probe.
const getHead = (): Promise<Result.Result<unknown, ExternalError>> =>
    request("/head", { method: "GET" });

// `POST /snapshot` — load a state snapshot (VCR replacement). `jsonText` is the
// raw text read off an uploaded `.snapshot.json` file, passed through verbatim;
// the server parses it at the boundary (`fromSnapshot`) and appends the
// `snapshot_loaded` genesis event. Same `{ok,value|error}` envelope as every
// other route — a malformed upload comes back as a typed `ok:false` refusal.
const postSnapshot = (
    jsonText: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request("/snapshot", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: jsonText,
    });

// === Auth =====================================================================
// `/auth/me` is the ONE route where a 401 is NOT a transport fault: it's the
// ANONYMOUS answer (no or invalid session). The auth surface is always mounted
// (the server is always authenticated), so this route bypasses the shared
// `request`/`fromStatus` mapping (which folds 401 → Unauthorized) and reads the
// raw status into a typed probe; only a genuine transport failure (offline /
// non-JSON body) yields an ExternalError.
type AuthProbe =
    | { readonly _tag: "authed"; readonly value: unknown }
    | { readonly _tag: "anonymous" };

const getAuthMe = async (): Promise<
    Result.Result<AuthProbe, ExternalError>
> => {
    const timeout = abortAfter(REQUEST_TIMEOUT_MS);
    let res: Response;
    try {
        res = await fetch(`${API_BASE}/auth/me`, {
            method: "GET",
            signal: timeout.signal,
        });
    } catch (e) {
        timeout.clear();
        return Result.err(mkUnreachable(messageOf(e)));
    }
    timeout.clear();
    // 401 = not signed in. 404 is defensive (the auth surface is always
    // mounted now) → still "not signed in", so land on the sign-in gate.
    if (res.status === 401 || res.status === 404) {
        return Result.ok({ _tag: "anonymous" });
    }
    let body: unknown;
    try {
        body = await res.json();
    } catch (e) {
        return res.ok
            ? Result.err(mkMalformed(messageOf(e)))
            : Result.err(mkUnreachable(`HTTP ${res.status} non-JSON body`));
    }
    if (!res.ok) return Result.err(fromStatus(res.status, body));
    const env = readEnvelope(body);
    return Result.isErr(env)
        ? env
        : Result.ok({ _tag: "authed", value: env.value });
};

// `POST /auth/logout` — clears the session cookie server-side.
const logout = (): Promise<Result.Result<unknown, ExternalError>> =>
    request("/auth/logout", { method: "POST" });

// === Workspace access (control plane) =========================================
// `GET /workspaces`, `GET|POST /workspaces/:id/members`,
// `DELETE /workspaces/:id/members/:userId` — see packages/server/src/routes/
// workspaces.ts. Same `{ok,value}` envelope as every other route.

const getWorkspaces = (): Promise<Result.Result<unknown, ExternalError>> =>
    request("/workspaces", { method: "GET" });

const getWorkspaceMembers = (
    workspaceId: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/workspaces/${encodeURIComponent(workspaceId)}/members`, {
        method: "GET",
    });

const inviteWorkspaceMember = (
    workspaceId: string,
    email: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/workspaces/${encodeURIComponent(workspaceId)}/members`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
    });

const removeWorkspaceMember = (
    workspaceId: string,
    userId: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(
        `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
        { method: "DELETE" },
    );

// `POST /account/delete` — GDPR hard-delete of the caller's own account +
// workspace + sessions. See packages/server/src/routes/account.ts.
const deleteAccount = (): Promise<Result.Result<unknown, ExternalError>> =>
    request("/account/delete", { method: "POST" });

// `GET /templates` — the built-in template-shape catalogue (unguarded; the
// shapes carry no workspace data). Same envelope as every other route.
const getTemplates = (): Promise<Result.Result<unknown, ExternalError>> =>
    request("/templates", { method: "GET" });

// `GET /search?q=` — content search over source extracts + material text
// (library C3). Same `{ ok, value }` envelope; the caller parses the hits.
const getSearch = (
    query: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/search?q=${encodeURIComponent(query)}`, { method: "GET" });

// `GET /claims?source=|goal=|concept=` — the knowledge bank's claims for
// one source, goal or concept (knowledge-bank.md §11.9). `key` names which.
const getClaims = (
    key: "source" | "goal" | "concept",
    id: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/claims?${key}=${encodeURIComponent(id)}`, { method: "GET" });

// === History & Inspection reads ===============================================
// The event log IS the version store: these fold the retained envelope stream
// server-side (whole-log timeline / per-resource version list / fold-to-
// marker reconstruction). Same `{ ok, value }` envelope as every other route.

// `GET /admin/ai-usage/<path>` — the operator's AI-usage reads (#86). The
// server answers 403 to anyone not an admin.
// `/admin/<path>` — the operator's Ops reads and actions: AI usage (#86),
// feedback (#84), accounts, the environment. The server answers 403 to
// anyone not an admin.
const getAdmin = (
    path: string,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/admin/${path}`, { method: "GET" });

const postAdmin = (
    path: string,
    body: unknown = {},
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/admin/${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
    });

const getHistory = (
    headers?: Readonly<Record<string, string>>,
): Promise<Result.Result<unknown, ExternalError>> =>
    request("/history", { method: "GET", headers: { ...(headers ?? {}) } });

const getFileHistory = (
    resourceId: string,
    headers?: Readonly<Record<string, string>>,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/history/resources/${encodeURIComponent(resourceId)}`, {
        method: "GET",
        headers: { ...(headers ?? {}) },
    });

// One resource's OWN folded moments — the Journal detail pane's per-entity
// mini-journal. Same `{ ok, value }` envelope; the caller parses the moment
// rows.
const getEntityHistory = (
    resourceId: string,
    headers?: Readonly<Record<string, string>>,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(`/history/resources/${encodeURIComponent(resourceId)}/moments`, {
        method: "GET",
        headers: { ...(headers ?? {}) },
    });

// Fold-to-marker: the reconstructed post-image + head hash for one version.
const getResourceVersion = (
    resourceId: string,
    marker: string,
    headers?: Readonly<Record<string, string>>,
): Promise<Result.Result<unknown, ExternalError>> =>
    request(
        `/history/resources/${encodeURIComponent(resourceId)}/versions/${encodeURIComponent(marker)}`,
        { method: "GET", headers: { ...(headers ?? {}) } },
    );

// `GET /resources/:id/original` — the source's ORIGINAL ingested bytes (raw
// asset response, not the JSON envelope). `null` is the honest common case,
// not a fault: text sources store no blob, pre-blob-store ingests have none,
// and postgres mode serves none — the caller renders the extracted text
// instead (graceful blob degradation). Only a 200 with bytes yields a value.
type OriginalBytes = {
    readonly data: Uint8Array;
    readonly mediaType: string;
};

// The same-origin URL of a source's ORIGINAL bytes. A native element
// (`<img>`/`<audio>`/`<video>`) can point its `src` straight at this — the
// browser fetches+renders with the endpoint's own content-type, and the
// session cookie rides the same-origin request. One place builds the path, so
// it stays in step with `getSourceOriginal` above.
const sourceOriginalUrl = (sourceId: string): string =>
    `${API_BASE}/resources/${encodeURIComponent(sourceId)}/original`;

const getSourceOriginal = async (
    sourceId: string,
): Promise<OriginalBytes | null> => {
    // Same timeout guard (D3): a hung blob read degrades to null (the abort
    // lands in the catch), so the source view falls back to extracted text
    // instead of stalling.
    const timeout = abortAfter(REQUEST_TIMEOUT_MS);
    try {
        const res = await fetch(
            `${API_BASE}/resources/${encodeURIComponent(sourceId)}/original`,
            { signal: timeout.signal },
        );
        // Only a 200 carries bytes. 204 (no blob — the expected common case) and
        // any error status degrade to null → the caller renders extracted text.
        if (res.status !== 200) return null;
        const buf = await res.arrayBuffer();
        return {
            data: new Uint8Array(buf),
            mediaType:
                res.headers.get("content-type") ?? "application/octet-stream",
        };
    } catch {
        return null;
    } finally {
        timeout.clear();
    }
};

// === Helpers ==================================================================

// Every API request carries the chosen UI locale as `Accept-Language`
// (FR-11.2): the server writes agent text and summaries in it. Read from the
// active Lingui instance, so a language switch applies to the next request.
// Before activation the browser's own header is left alone.
const withUiLocale = (headers: HeadersInit | undefined): Headers => {
    const h = new Headers(headers);
    if (isLocale(i18n.locale)) h.set("accept-language", i18n.locale);
    return h;
};

const messageOf = (e: unknown): string =>
    e instanceof Error ? e.message : String(e);

const safeStringify = (v: unknown): string => {
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
};
